import React from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    ThemeProvider,
    StyledEngineProvider,
} from '@mui/material';
import Form, { FormProps } from './Form/Form';
import { getThemeForMode } from "./../../themes/ThemeBlue";
import { useCbxTheme } from '../../reducer/theme';

export interface FormDialogProps<T extends Record<string, any>> extends FormProps<T> {
    show: boolean;
    dismiss: () => void;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
    fullWidth?: boolean;
    fullHeight?: boolean;
    title?: React.ReactNode;
    disableBackdropClick?: boolean;
    disableEscapeKeyDown?: boolean;
    dialogStyle?: React.CSSProperties;
    dialogRootStyle?: React.CSSProperties;
    dialogTitleStyle?: React.CSSProperties;
    hideBackdrop?: boolean;
    disableScrollLock?: boolean;
    paperRef?: React.Ref<HTMLDivElement>;
}

export default function FormDialog<T extends Record<string, any>>(props: FormDialogProps<T>) {
    const cbxTheme = useCbxTheme();
    const theme = getThemeForMode(cbxTheme);
    const { title, show, dismiss, maxWidth, fullWidth, disableBackdropClick, disableEscapeKeyDown, fullHeight,
        onSubmit, initialValues, enableReinitialize, validate, validationSchema, postValidation, style, children,
        dialogStyle, dialogRootStyle, hideBackdrop, disableScrollLock, paperRef, dialogTitleStyle } = props;
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <Dialog
                    open={!!show}
                    aria-labelledby="responsive-dialog-title"
                    onClose={(ev, reason) => {
                        if (reason === 'backdropClick' && disableBackdropClick === true) return;
                        dismiss();
                    }}
                    maxWidth={maxWidth || false}
                    fullWidth={fullWidth || false}
                    disableEscapeKeyDown={disableEscapeKeyDown || false}
                    hideBackdrop={hideBackdrop || false}
                    disableScrollLock={disableScrollLock || false}
                    style={dialogRootStyle}
                    PaperProps={{
                        ref: paperRef,
                        style: {
                            overflowY: 'unset',
                            height: fullHeight === true ? '100%' : undefined,
                            ...dialogStyle
                        }
                    }}
                >
                    {title && <DialogTitle id="responsive-dialog-title" style={dialogTitleStyle}>{title}</DialogTitle>}
                    <DialogContent style={{ overflowY: 'unset', display: 'flex', flexDirection: 'column' }}>
                        <Form<T>
                            style={style}
                            onSubmit={onSubmit}
                            postValidation={postValidation}
                            initialValues={initialValues}
                            enableReinitialize={enableReinitialize}
                            validate={validate}
                            validationSchema={validationSchema}>
                            {children}
                        </Form>
                    </DialogContent>
                </Dialog>
            </ThemeProvider>
        </StyledEngineProvider>
    );
}

export function FormDialogNoTheme<T extends Record<string, any>>(props: FormDialogProps<T>) {
    const { title, show, dismiss, maxWidth, fullWidth, disableBackdropClick, disableEscapeKeyDown, fullHeight,
        onSubmit, initialValues, enableReinitialize, validate, validationSchema, postValidation, style, children,
        dialogStyle, dialogRootStyle, hideBackdrop, disableScrollLock, paperRef } = props;
    return (
        <Dialog
            open={!!show}
            aria-labelledby="responsive-dialog-title"
            onClose={(ev, reason) => {
                if (reason === 'backdropClick' && disableBackdropClick === true) return;
                dismiss();
            }}
            maxWidth={maxWidth || false}
            fullWidth={fullWidth || false}
            disableEscapeKeyDown={disableEscapeKeyDown || false}
            hideBackdrop={hideBackdrop || false}
            disableScrollLock={disableScrollLock || false}
            style={dialogRootStyle}
            PaperProps={{
                ref: paperRef,
                style: {
                    overflowY: 'unset',
                    height: fullHeight === true ? '100%' : undefined,
                    ...dialogStyle
                }
            }}
        >
            {title && <DialogTitle id="responsive-dialog-title">{title}</DialogTitle>}
            <DialogContent style={{ overflowY: 'unset', display: 'flex', flexDirection: 'column' }}>
                <Form<T>
                    style={style}
                    onSubmit={onSubmit}
                    postValidation={postValidation}
                    initialValues={initialValues}
                    enableReinitialize={enableReinitialize}
                    validate={validate}
                    validationSchema={validationSchema}>
                    {children}
                </Form>
            </DialogContent>
        </Dialog>
    );
}
